// ---------------- Helper Functions ----------------

// Safe render
export function safeRender(val: any): string {
  if (val === null || val === undefined) return '-';
  return typeof val === 'object' ? JSON.stringify(val) : String(val);
}

// Format Date eg., 2000-05-02
export function formatDate(dateString: string): string {
  const d = new Date(dateString);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

// Format Time eg., 6:00 & 14:00
export function formatTime12to24(time: string): string {
  if (!time) return '00:00';

  const [timePart, modifier] = time.split(' ');
  let [hours = 0, minutes = 0] = timePart ? timePart.split(':').map(Number) : [];

  if (modifier?.toUpperCase() === 'PM' && hours < 12) hours += 12;
  if (modifier?.toUpperCase() === 'AM' && hours === 12) hours = 0;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// Calculate duration
export function calculateDuration(start_date: string, end_date: string): string {
  const parseDate = (dateStr: string): Date => {
    const parts = String(dateStr || '')
      .trim()
      .split(/\s+/);
    const datePart = parts[0] || '';
    const timePart = parts[1] || '';

    const [day = 0, month = 1, year = 1970] = datePart.split('-').map(Number);

    let hours = 0;
    let minutes = 0;

    if (timePart) {
      const timeSplits = timePart.split(':').map(Number);
      hours = timeSplits[0] || 0;
      minutes = timeSplits[1] || 0;
    }

    return new Date(year, month - 1, day, hours, minutes);
  };

  const start = parseDate(start_date);
  const end = parseDate(end_date);

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  if (days < 0) {
    months--;
    const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
    days += prevMonth.getDate();
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} Year${years > 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} Month${months > 1 ? 's' : ''}`);
  if (days > 0) parts.push(`${days} Day${days > 1 ? 's' : ''}`);

  return parts.join(', ') || '-';
}

// Format time (decimal)
export const formatTime = (decimal: number | string) => {
  if (typeof decimal === 'number') {
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    const h = hours % 24;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const formattedH = h % 12 === 0 ? 12 : h % 12;
    const formattedM = minutes.toString().padStart(2, '0');
    return `${formattedH}:${formattedM} ${ampm}`;
  }
  return decimal;
};

// Date formatter
export function formatShowDate(dateString: string): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

// Time formatter
export function formatShowTime(timeString: string): string {
  if (!timeString) return '';

  const [hourStr = '', minuteStr = ''] = timeString.split(':');
  const hours = parseInt(hourStr, 10);
  const minutes = parseInt(minuteStr, 10);

  if (isNaN(hours) || isNaN(minutes)) return '';

  const date = new Date();
  date.setHours(hours, minutes);

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// Timezone formatter eg., 5.5 -> GMT +05:30
export function formatTimezone(timezone: number | string): string {
  if (timezone === null || timezone === undefined || timezone === '') return '-';
  const tz = typeof timezone === 'string' ? parseFloat(timezone) : timezone;
  if (isNaN(tz)) return String(timezone);

  const absTz = Math.abs(tz);
  const hours = Math.floor(absTz);
  const minutes = Math.round((absTz - hours) * 60);
  const sign = tz >= 0 ? '+' : '-';

  return `GMT ${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}
